import { useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  DateFormat,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  Tab,
  TextInputV2,
} from '@equip.health/ui';
import type { DateTime } from 'luxon';

import { GroupClassEvents } from '~/analytics/groupClassEvents';
import {
  GroupClassTabTitle,
  type GroupClassListTab,
  GROUP_CLASS_SEARCH_PLACEHOLDER_BY_TAB,
} from '~/lib/constants/groupClasses.constants';
import GroupClassDetailsModal from '~/components/maud/calendar/group-class/modals/GroupClassDetailsModal';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useLazyGetGroupClassDetailQuery } from '~/store/services/groupClass';
import type { GroupsTabProps } from '~/components/maud/calendar/groupsV2/GroupsWrapper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CreateEditGroupModal from '~/components/maud/calendar/groupsV2/modals/CreateEditGroup';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';

const TOP_LEVEL_ID = 'calendar-schedule-changer-V2';

export enum GroupClassWeekViewPagination {
  NEXT = 'next',
  PREVIOUS = 'previous',
}

// Todo: below enum should be removed in favor of GroupsListTypeOptions in GroupsWrapper
export enum GroupClassListTypeOptions {
  ACTIVE = 'active',
  ARCHIVE = 'archive',
  MY_GROUPS = 'my-groups',
  WEEKLY = 'weekly',
}

export enum GroupsListTypeOptions {
  ALL_GROUPS = 'all-groups',
  MY_SECTIONS = 'my-sections',
  WEEKLY = 'weekly',
  ARCHIVE = 'archive',
}

export enum GroupSectionTabTitle {
  ALL_GROUPS = 'All groups',
  MY_SECTIONS = 'My sections',
  WEEKLY = 'Weekly view',
  ARCHIVE = 'Archive',
}

export interface CalendarScheduleTabProps {
  count?: number;
  listType: GroupClassListType;
  tabCount?: number;
  title: string;
}

interface ScheduleChangerProps {
  currentDate: DateTime;
  groupClassTitle: string;
  setGroupClassTitle: (title: string) => void;
  onCreateClass: () => void;
  onDateChange: (direction: GroupClassWeekViewPagination) => void;
  onTabChanged: (tabIndex: GroupClassListType) => void;
  tabs?: Record<GroupClassListTab, CalendarScheduleTabProps>; // todo: remove this in favor of tabsV2
  tabsV2?: Record<GroupsListTypeOptions, GroupsTabProps>;
}

const ScheduleChangerV2 = ({
  currentDate,
  groupClassTitle,
  onCreateClass,
  onDateChange,
  onTabChanged,
  setGroupClassTitle,
  tabs,
  tabsV2,
}: ScheduleChangerProps) => {
  const [selectedTab, setSelectedTab] = useState<GroupClassListType>(
    tabsV2
      ? GroupsListTypeOptions.ALL_GROUPS
      : GroupClassListTypeOptions.MY_GROUPS,
  );

  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const isNewDesign = isGroupsRestructuringEnabled && !!tabsV2;

  const { trackEvent } = useAnalytics();
  const { permissions } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const { setComponentVisibility } = useComponentPortalContext();
  const [isGroupClassDetailModalOpen, setIsGroupClassDetailModalOpen] =
    useState<boolean>(false);

  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);

  const [getGroupClassDetails, { data: groupClassDetails }] =
    useLazyGetGroupClassDetailQuery();

  const handleGroupClassCreationSuccess = ({
    id,
    isViewClass,
  }: {
    id: string;
    isViewClass: boolean;
  }) => {
    onCreateClass();
    if (isViewClass) {
      getGroupClassDetails({ groupClassExternalId: id });
      setIsGroupClassDetailModalOpen(true);
    }
  };

  const showAddGroupClassAccordion = () => {
    setComponentVisibility({
      component: PortalComponent.ADD_GROUP_CLASS_ACCORDION,
      data: { handleGroupClassCreationSuccess },
      isExpanded: true,
      isVisible: true,
    });
    trackEvent(GroupClassEvents.createGroupButtonClicked, {
      Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
    });
  };

  const handleCreateGroupClick = () => {
    if (isNewDesign) {
      setIsCreateGroupModalOpen(true);
      trackEvent(GroupSectionEvents.createGroupV2ButtonClicked, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      });
    } else {
      showAddGroupClassAccordion();
    }
  };

  const handleWeekChange = (direction: GroupClassWeekViewPagination) => {
    onDateChange(direction);
  };

  const handleEventToTrack = (source: string) => {
    const eventMap = tabsV2
      ? {
          [GroupSectionTabTitle.MY_SECTIONS]:
            GroupSectionEvents.mySectionsTabButtonClicked,
          [GroupSectionTabTitle.ALL_GROUPS]:
            GroupSectionEvents.allGroupsTabButtonClicked,
          [GroupSectionTabTitle.WEEKLY]:
            GroupSectionEvents.weeklyViewTabButtonClicked,
          [GroupSectionTabTitle.ARCHIVE]:
            GroupSectionEvents.archiveTabButtonClicked,
        }
      : {
          [GroupClassTabTitle.MY_GROUPS]:
            GroupClassEvents.myGroupsTabButtonClicked,
          [GroupClassTabTitle.ACTIVE_GROUPS]:
            GroupClassEvents.activeGroupsTabButtonClicked,
          [GroupClassTabTitle.WEEKLY_VIEW]:
            GroupClassEvents.weeklyViewTabButtonClicked,
          [GroupClassTabTitle.ARCHIVE]:
            GroupClassEvents.archiveTabButtonClicked,
        };
    const eventToTrack = eventMap[source];
    if (!eventToTrack) return;
    trackEvent(eventToTrack, {
      Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
    });
  };

  const renderableTabs = tabs ?? tabsV2 ?? undefined;

  return (
    <ChakraFlex
      direction="column"
      id={TOP_LEVEL_ID}
      marginTop="20px"
      position="relative"
    >
      <ChakraFlex
        align="center"
        justify="space-between"
        paddingBottom="24px"
        paddingRight="24px"
      >
        <ChakraFlex align="center">
          {renderableTabs &&
            Object.values(renderableTabs).map((tab) => (
              <Tab
                counter={tab.tabCount}
                isActive={selectedTab === tab.listType}
                key={tab.title}
                onClick={() => {
                  setSelectedTab(tab.listType);
                  setGroupClassTitle('');
                  onTabChanged(tab.listType);
                  handleEventToTrack(tab.title);
                }}
                title={tab.title}
                variant="bubble"
              />
            ))}
        </ChakraFlex>
        <ChakraFlex>
          {selectedTab == GroupClassListTypeOptions.WEEKLY && (
            <ChakraFlex align="center" margin="auto" marginRight="18px">
              <ChakraText color="primary.90.variant" marginRight="18px">
                {currentDate.toFormat(DateFormat.fullMonthYear)}
              </ChakraText>

              <ChakraFlex>
                <ChakraBox cursor="pointer" margin="auto" marginRight="10px">
                  <FireflyIconButton
                    icon={FAIconName.arrowLeftRegular}
                    id={`${TOP_LEVEL_ID}__create-group-class-weekely-leftarrow`}
                    onClick={() =>
                      handleWeekChange(GroupClassWeekViewPagination.PREVIOUS)
                    }
                    variant="tertiary"
                  />
                </ChakraBox>
                <ChakraBox cursor="pointer" margin="auto">
                  <FireflyIconButton
                    icon={FAIconName.arrowRightRegular}
                    id={`${TOP_LEVEL_ID}__create-group-class-weekely-rightarrow`}
                    onClick={() =>
                      handleWeekChange(GroupClassWeekViewPagination.NEXT)
                    }
                    variant="tertiary"
                  />
                </ChakraBox>
              </ChakraFlex>
            </ChakraFlex>
          )}
          <ChakraFlex gap="18px">
            {selectedTab !== GroupClassListTypeOptions.WEEKLY && (
              <TextInputV2
                id={`${TOP_LEVEL_ID}__group-class-search-input`}
                isReset
                leftIcon={FAIconName.searchRegular}
                onChange={setGroupClassTitle}
                placeholder={
                  GROUP_CLASS_SEARCH_PLACEHOLDER_BY_TAB[
                    selectedTab
                      .toUpperCase()
                      .replace(
                        '-',
                        '_',
                      ) as keyof typeof GROUP_CLASS_SEARCH_PLACEHOLDER_BY_TAB
                  ]
                }
                value={groupClassTitle}
              />
            )}

            {isSchedulingAdmin && (
              <FireflyButton
                id={`${TOP_LEVEL_ID}__create-group-class-btn_new`}
                leftIcon={FAIconName.plusSolid}
                onClick={handleCreateGroupClick}
              >
                Create group
              </FireflyButton>
            )}
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
      {groupClassDetails?.data && (
        <GroupClassDetailsModal
          groupClass={groupClassDetails.data}
          isOpen={isGroupClassDetailModalOpen}
          onClose={() => setIsGroupClassDetailModalOpen(false)}
        />
      )}

      <CreateEditGroupModal
        isOpen={isCreateGroupModalOpen}
        onClose={() => setIsCreateGroupModalOpen(false)}
        handleSuccess={onCreateClass}
      />
    </ChakraFlex>
  );
};

export default ScheduleChangerV2;
